import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Panel, Field, NSelect, Loading, Empty, Plate, Chip, useFetch } from "@/components/common";
import { Gallery, PhotoPicker, useUploadsPending } from "@/components/Photos";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { AccStatus, ACC_STATUS, RESP } from "@/pages/livraison/Accidents";
import { CATS } from "@/pages/livraison/AccidentForm";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fDate, fEur } from "@/lib/api";

const yn = (b) => (b ? "Oui" : "Non");
const ALERT_TXT = { not_declared: "Non déclaré à l'assurance", no_number: "Dossier assurance sans numéro", expertise: "Expertise en attente", repair: "Réparation en attente", refund: "Remboursement en attente", deductible: "Franchise renseignée", too_old: "Ouvert depuis plus de 60 jours" };
const INS = [["declared_to_insurer", "Sinistre déclaré à l'assurance", "bool"], ["insurer_declared_date", "Date de déclaration à l'assurance", "date"], ["insurer", "Assureur", "text"],
  ["insurance_number", "N° dossier / sinistre assurance", "text"], ["expert", "Expert éventuel", "text"], ["expertise_date", "Date d'expertise", "date"],
  ["garage", "Garage / réparateur", "text"], ["repair_date", "Date de réparation", "date"], ["quote_amount", "Montant du devis (€)", "number"],
  ["repair_amount", "Facture réparation (€)", "number"], ["deductible", "Franchise (€)", "number"], ["insurer_covered", "Pris en charge par l'assurance (€)", "number"]];
const REF = [["refund_expected", "Remboursement attendu", "bool"], ["refund_received", "Remboursement reçu", "bool"], ["refund_date", "Date du remboursement", "date"],
  ["refund_amount", "Montant remboursé (€)", "number"], ["refund_ref", "Mode / référence de règlement", "text"]];
const ALL = [...INS, ...REF];
const toIn = (v, t) => (t === "bool" ? (v === true ? "oui" : v === false ? "non" : "") : v ?? "");
const fromIn = (v, t) => (v === "" ? null : t === "bool" ? v === "oui" : t === "number" ? Number(v) : v);
const show = (v, t) => (v == null ? "—" : t === "bool" ? (v ? "Oui" : "Non") : t === "number" ? fEur(v) : t === "date" ? fDate(v) : v);

function InsField({ k, l, t, f, setF }) {
  if (t === "bool") return <Field label={l}><NSelect value={f[k]} onChange={(v) => setF({ ...f, [k]: v })} placeholder="—" testid={`suivi-${k}`} options={[{ value: "oui", label: "Oui" }, { value: "non", label: "Non" }]} /></Field>;
  return <Field label={l}><Input type={t} step="0.01" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-10 bg-white" data-testid={`suivi-${k}`} /></Field>;
}

function Complement({ a, onSaved }) {
  const [desc, setDesc] = useState(a.description);
  const [photos, setPhotos] = useState([]);
  const uploading = useUploadsPending();
  const save = async () => {
    try {
      onSaved((await api.patch(`/accidents/${a.id}/declaration`, { description: desc, add_photos: photos })).data);
      setPhotos([]);
      toast.success("Déclaration complétée");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Compléter la déclaration" className="mb-6" testid="accident-complement">
      <Field label="Description des faits"><Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} className="bg-white" data-testid="complement-description" /></Field>
      <div className="mt-4"><PhotoPicker items={photos} onChange={setPhotos} categories={CATS} testid="complement-photos" /></div>
      <Button onClick={save} disabled={uploading > 0} className="mt-4 bg-[#0E2D43] text-white" data-testid="complement-save">{uploading ? `Envoi des photos (${uploading})…` : "Enregistrer"}</Button>
    </Panel>
  );
}

function Suivi({ a, onSaved }) {
  const [f, setF] = useState({ status: a.status, note: "", ...Object.fromEntries(ALL.map(([k, , t]) => [k, toIn(a[k], t)])) });
  const save = async () => {
    const body = { status: f.status, note: f.note };
    ALL.forEach(([k, , t]) => { body[k] = fromIn(f[k], t); });
    try {
      onSaved((await api.patch(`/accidents/${a.id}/suivi`, body)).data);
      setF((x) => ({ ...x, note: "" }));
      toast.success("Suivi mis à jour");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Panel title="Suivi assurance (Direction / administrateur)" className="mb-6" testid="accident-suivi">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Statut du dossier"><NSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="suivi-status-select" options={Object.entries(ACC_STATUS).map(([k, x]) => ({ value: k, label: x[0] }))} /></Field>
        {INS.map(([k, l, t]) => <InsField key={k} k={k} l={l} t={t} f={f} setF={setF} />)}
      </div>
      <div className="text-[11px] uppercase font-bold tracking-wider text-slate-500 mt-5 mb-2">Remboursement</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">{REF.map(([k, l, t]) => <InsField key={k} k={k} l={l} t={t} f={f} setF={setF} />)}</div>
      <div className="mt-3"><Field label="Commentaire de suivi"><Textarea rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="bg-white" data-testid="suivi-note" /></Field></div>
      <Button onClick={save} className="mt-4 bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="suivi-save">Enregistrer le suivi</Button>
    </Panel>
  );
}

function Notifs({ a, onDone, canResend }) {
  const [busy, setBusy] = useState(false);
  const list = a.notifications || [];
  const latest = {};
  list.forEach((n) => { latest[n.to] = n; });
  const failed = Object.values(latest).filter((n) => n.status === "echec");
  const resend = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/accidents/${a.id}/notify`);
      data.status === "envoye" ? toast.success("Notification envoyée") : toast.error("Nouvel échec de l'envoi");
      const r = await api.get(`/accidents/${a.id}`);
      onDone(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Notification email" className="mb-6" testid="accident-notifications">
      {failed.length > 0 && <div className="mb-3 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm font-semibold" data-testid="accident-notif-alert">Échec de l'envoi de la notification à {failed.map((n) => n.to).join(", ")}</div>}
      {!list.length ? <p className="text-sm text-slate-500">Envoi en cours ou aucune notification enregistrée.</p> : (
        <ul className="space-y-1 text-sm">{list.map((n, i) => (
          <li key={i} data-testid={`accident-notif-${i}`}>{new Date(n.date).toLocaleString("fr-FR")} — {n.to} — <b className={n.status === "envoye" ? "text-emerald-700" : "text-rose-700"}>{n.status === "envoye" ? "Envoi réussi" : "Échec"}</b>{n.by !== "Automatique" ? ` (renvoyé par ${n.by})` : ""}</li>
        ))}</ul>
      )}
      {canResend && <Button variant="outline" size="sm" className="mt-3" onClick={resend} disabled={busy} data-testid="accident-notif-resend">Renvoyer la notification</Button>}
    </Panel>
  );
}

export default function AccidentFiche() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const admin = user.canEdit;
  const full = user.role === "admin";
  const { data: a, loading, setData, error } = useFetch(`/accidents/${id}`);
  const [del, setDel] = useState(false);
  const [confirm, setConfirm] = useState("");
  if (loading) return <Loading />;
  if (!a) return <Empty text={error || "Dossier introuvable"} />;
  const remove = async () => {
    try {
      await api.delete(`/accidents/${a.id}`, { params: { confirm } });
      toast.success("Dossier supprimé");
      nav(back);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const com = a.module === "commercial";
  const back = com ? "/commerciaux/accidents" : "/livraison/accidents";
  const facts = [["Date et heure", `${fDate(a.date)} à ${a.time}`], ["Lieu", a.location], ["Déclaration enregistrée par", a.declared_by_name],
    ["Tiers impliqué", yn(a.third_party)], ["Accident responsable", RESP[a.responsible]], ["Véhicule encore roulant", yn(a.drivable)],
    ["Réparation nécessaire", yn(a.repair_needed)], ["Constat amiable", yn(a.amicable)], ["Forces de l'ordre", yn(a.police)]];
  return (
    <div data-testid="accident-fiche">
      <Link to={back} className="inline-flex items-center gap-1 text-sm text-slate-500 mb-4" data-testid="accident-back"><ArrowLeft className="w-4 h-4" /> Accidents / Sinistres</Link>
      <PageHeader overline={`Dossier ${a.number}`} title="Fiche accident">
        <AccStatus v={a.status} />
        {admin && <Button variant="outline" onClick={() => setDel(true)} className="border-rose-300 text-rose-700" data-testid="accident-delete-button"><Trash2 className="w-4 h-4 mr-1" /> Suppression exceptionnelle</Button>}
      </PageHeader>
      {a.vehicle_immobilized && <div className="mb-4 text-sm bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3" data-testid="accident-immobilized-note">Véhicule passé automatiquement au statut « Immobilisé » lors de la déclaration.</div>}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <Panel title="Véhicule et chauffeur" testid="accident-identity">
          <div className="space-y-2 text-sm">
            <div><Link to={com ? `/commerciaux/vehicules/${a.vehicle_id}` : `/livraison/vehicules/${a.vehicle_id}`}><Plate>{a.plate}</Plate></Link> <span className="text-slate-500">{a.vehicle_label}</span></div>
            <div>{a.module === "commercial" ? "Commercial" : "Chauffeur"} : {full && a.module !== "commercial" ? <Link to={`/livraison/chauffeurs/${a.driver_id}`} className="font-semibold hover:underline" data-testid="accident-driver-link">{a.driver_name}</Link> : <b>{a.driver_name}</b>}</div>
          </div>
        </Panel>
        <Panel title="Faits" className="lg:col-span-2" testid="accident-facts">
          <p className="text-sm mb-3 whitespace-pre-line" data-testid="accident-description">{a.description}</p>
          {a.comment && <p className="text-sm text-slate-500 mb-3">{a.comment}</p>}
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
            {facts.map(([l, v]) => <div key={l}><dt className="text-[11px] uppercase font-bold tracking-wider text-slate-400">{l}</dt><dd className="font-semibold text-[#0E2D43]">{v}</dd></div>)}
          </dl>
        </Panel>
      </div>
      <Panel title={`Photos des dégâts (${a.photos.length})`} className="mb-6" testid="accident-gallery">
        <Gallery ids={a.photos.map((p) => p.file_id)} labels={a.photos.map((p) => CATS[p.category] || "")} testid="accident-photos" />
      </Panel>
      {full && (
        <Panel title="Assurance / réparation / remboursement" className="mb-6" testid="accident-insurance">
          {a.alerts?.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-testid="accident-alerts">{a.alerts.map((x) => <Chip key={x} cls="bg-amber-50 text-amber-800 border-amber-300">{ALERT_TXT[x]}</Chip>)}</div>}
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {ALL.map(([k, l, t]) => <div key={k}><dt className="text-[11px] uppercase font-bold tracking-wider text-slate-400">{l}</dt><dd className="font-semibold">{show(a[k], t)}</dd></div>)}
          </dl>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-slate-50 border p-3"><div className="text-[11px] uppercase font-bold text-slate-500">Coût total du sinistre</div><div className="text-lg font-bold text-[#0E2D43]" data-testid="accident-total-cost">{fEur(a.cost)}</div></div>
            <div className="rounded-lg bg-[#0E2D43] text-white p-3"><div className="text-[11px] uppercase font-bold text-white/70">Reste à charge réel Prest'hyg</div><div className="text-lg font-bold" data-testid="accident-remaining-cost">{fEur(a.remaining_cost)}</div></div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Coût total = facture (ou devis à défaut). Reste à charge = coût total − remboursement reçu ; à défaut − prise en charge assurance ; à défaut la franchise si le sinistre est déclaré.</p>
        </Panel>
      )}
      {user.real_role !== "direction" && (a.status !== "clos" || admin) && <Complement a={a} onSaved={setData} key={`c-${a.history.length}`} />}
      {full && <Suivi a={a} onSaved={setData} key={`s-${a.history.length}`} />}
      {full && <Notifs a={a} onDone={setData} canResend={admin} />}
      <Panel title="Historique des modifications" testid="accident-history">
        <ul className="space-y-2 text-sm">{[...a.history].reverse().map((h, i) => (
          <li key={i} className="border-l-2 border-[#F49619] pl-3"><b>{h.action}</b> — {h.by} · {new Date(h.date).toLocaleString("fr-FR")}{h.note ? <div className="text-slate-500">{h.note}</div> : null}</li>
        ))}</ul>
      </Panel>
      {del && (
        <ConfirmDialog title="Suppression exceptionnelle" onConfirm={remove} onClose={() => setDel(false)} testid="accident-delete-dialog">
          <p>Le dossier sera retiré des listes. Saisissez le numéro <b>{a.number}</b> pour confirmer.</p>
          {a.vehicle_immobilized && <p className="text-rose-700">Le véhicule reste au statut « Immobilisé » : modifiez-le si nécessaire dans Paramètres → Véhicules.</p>}
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="accident-delete-confirm-input" />
        </ConfirmDialog>
      )}
    </div>
  );
}
